import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './MyListings.css';
import { apiUrl } from '../api';

function MyListings({ token }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit Modal States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Tracks existing image URLs
  const [existingImages, setExistingImages] = useState([]);
  // Tracks new files selected via input
  const [newImageFiles, setNewImageFiles] = useState([]);
  // Tracks visual previews for new files
  const [newImagePreviews, setNewImagePreviews] = useState([]);

  const [editFormData, setEditFormData] = useState({
    id: '',
    title: '',
    description: '',
    price: '',
    locality: '',
    city: '',
    condition: '',
    is_sold: false,
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchUserProducts = async () => {
      try {
        const response = await axios.get(apiUrl('/api/products/user/listings'), {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setProducts(response.data);
        setError('');
      } catch (err) {
        setError('Failed to load your listings. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProducts();
  }, [token, navigate]);

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) {
      return;
    }

    try {
      await axios.delete(apiUrl(`/api/products/${productId}`), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setProducts((prev) => prev.filter((p) => p.id !== productId));
    } catch (err) {
      setError('Failed to delete listing. Please try again.');
    }
  };

  /**
   * OPEN MODAL & PRE-FILL
   */
  const handleOpenEditModal = (product) => {
    setEditFormData({
      id: product.id,
      title: product.title || '',
      description: product.description || '',
      price: product.price || '',
      locality: product.locality || '',
      city: product.city || '',
      condition: product.condition || 'Used',
      is_sold: product.is_sold || false,
    });

    // Handle existing images
    setExistingImages(Array.isArray(product.image_urls) ? product.image_urls : []);
    
    // Reset new upload states
    setNewImageFiles([]);
    setNewImagePreviews([]);
    
    setIsEditModalOpen(true);
  };

  /**
   * CLOSE MODAL & CLEANUP
   */
  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    // Crucial: Clean up object URLs to prevent memory leaks
    newImagePreviews.forEach(url => URL.revokeObjectURL(url));
  };

  /**
   * HANDLE TEXT/SELECT INPUTS
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const sanitizedValue = name === 'price' ? value.replace(/[^0-9]/g, '') : value;
    setEditFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : sanitizedValue
    }));
  };

  /**
   * HANDLE LOCAL IMAGE SELECTION (FILE UPLOADER)
   */
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      
      // Store actual files for submission
      setNewImageFiles((prev) => [...prev, ...filesArray]);

      // Generate visual previews (Object URLs)
      const previews = filesArray.map(file => URL.createObjectURL(file));
      setNewImagePreviews((prev) => [...prev, ...previews]);
    }
  };

  // Remove an existing network image from the listing
  const removeExistingImage = (indexToRemove) => {
    setExistingImages(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  // Remove a newly selected local image before uploading
  const removeNewImage = (indexToRemove) => {
    // Revoke the object URL to free memory
    URL.revokeObjectURL(newImagePreviews[indexToRemove]);

    setNewImagePreviews(prev => prev.filter((_, index) => index !== indexToRemove));
    setNewImageFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  /**
   * SAVE EDITS (REQUIRES FORMDATA FOR FILES)
   */
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setUpdating(true);

    /*
     * We must use FormData now because we are sending actual files.
     * Standard JSON (payload object) won't work for mixed data.
     */
    const formData = new FormData();
    formData.append('title', editFormData.title);
    formData.append('description', editFormData.description);
    formData.append('price', parseInt(editFormData.price, 10) || 0);
    formData.append('locality', editFormData.locality);
    formData.append('city', editFormData.city);
    formData.append('condition', editFormData.condition);
    formData.append('is_sold', editFormData.is_sold);

    // 1. Send the list of existing images we are KEEPING
    formData.append('existing_images', JSON.stringify(existingImages));

    // 2. Append NEW image files (compress in browser before upload)
    if (existingImages.length + newImageFiles.length > 7) {
      alert('A maximum of 7 images are allowed per ad. Remove some images before saving.');
      setUpdating(false);
      return;
    }

    const compressImage = (file, maxWidth = 1024, quality = 0.75) => new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const ratio = Math.min(1, maxWidth / img.width);
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * ratio);
          canvas.height = Math.round(img.height * ratio);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        } catch (err) { reject(err); }
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

    for (const file of newImageFiles) {
      try {
        const compressed = await compressImage(file, 1024, 0.75);
        formData.append('images', compressed);
      } catch (err) {
        formData.append('images', file);
      }
    }

    try {
      const response = await axios.put(
        apiUrl(`/api/products/${editFormData.id}`),
        formData, // Sending FormData instead of JSON payload
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data' // Required header for files
          }
        }
      );

      // Update state to immediately reflect changes in UI
      // Assuming backend returns the fully updated product object including new URLs
      const updatedProduct = response.data;

      setProducts((prevProducts) =>
        prevProducts.map((prod) => (prod.id === editFormData.id ? updatedProduct : prod))
      );

      handleCloseModal();
    } catch (err) {
      console.error('Update error details:', err.response?.data || err.message);
      alert(`Failed to update listing: ${err.response?.data?.message || 'Server error'}`);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="my-listings-page">
        <div className="loading">Loading your listings...</div>
      </div>
    );
  }

  return (
    <div className="my-listings-page">
      <div className="listings-header">
        <h1>My Listings</h1>
        <button onClick={() => navigate('/post')} className="post-new-btn">
          Post New Ad
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {products.length === 0 ? (
        <div className="empty-state">
          <p>You haven't posted any listings yet.</p>
          <button onClick={() => navigate('/post')} className="primary-btn">
            Create Your First Listing
          </button>
        </div>
      ) : (
        <div className="listings-grid">
          {products.map((product) => (
            <div key={product.id} className="listing-card">
              <div className="listing-image">
                <img
                  src={product.image_urls?.[0] || 'placeholder.jpg'}
                  alt={product.title}
                />
                {product.is_sold && <div className="sold-badge">Sold</div>}
              </div>

              <div className="listing-content">
                <h3>{product.title}</h3>
                <p className="description">{product.description ? product.description.substring(0, 100) : ''}...</p>
                <p className="price">₹{product.price}</p>
                <p className="location">
                  {product.locality}, {product.city}
                </p>
                <p className="condition">Condition: {product.condition}</p>
                <p className="meta">Posted: {product.created_at ? new Date(product.created_at).toLocaleDateString() : '—'}</p>
                <p className="meta">Expires: {product.expires_at ? new Date(product.expires_at).toLocaleDateString() : '—'}</p>

                <div className="listing-actions">
                  <button className="edit-btn" onClick={() => handleOpenEditModal(product)}>
                    Edit
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteProduct(product.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* REFACTORED MODERN EDIT MODAL */}
      {isEditModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-containerRefactored" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Listing</h2>
              <button className="close-btn" onClick={handleCloseModal}>
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="modal-form">
              
              {/* IMAGE UPLOADER SECTION */}
              <div className="form-group image-uploader-group">
                <label>Listing Images</label>
                
                <div className="image-preview-grid">
                  {/* 1. Render Existing Images */}
                  {existingImages.map((url, index) => (
                    <div key={`existing-${index}`} className="preview-item">
                      <img src={url} alt={`Existing ${index}`} />
                      <button type="button" className="remove-img-btn" onClick={() => removeExistingImage(index)}>&times;</button>
                      <span className="img-type-badge existing">Saved</span>
                    </div>
                  ))}

                  {/* 2. Render New Selected Images (Previews) */}
                  {newImagePreviews.map((url, index) => (
                    <div key={`new-${index}`} className="preview-item new-preview">
                      <img src={url} alt={`New Preview ${index}`} />
                      <button type="button" className="remove-img-btn" onClick={() => removeNewImage(index)}>&times;</button>
                      <span className="img-type-badge new">New</span>
                    </div>
                  ))}

                  {/* 3. The 'Add Images' Button Card */}
                  <label htmlFor="image-upload-input" className="add-image-card">
                    <div className="add-icon">+</div>
                    <div className="add-text">Add Images</div>
                    <input 
                      id="image-upload-input"
                      type="file" 
                      accept="image/*" 
                      multiple 
                      onChange={handleFileChange} 
                      style={{display: 'none'}}
                    />
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  name="title"
                  value={editFormData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., iPhone 13 Pro, 256GB"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price (₹)</label>
                  <input
                    type="number"
                    name="price"
                    value={editFormData.price}
                    onChange={handleInputChange}
                    onWheel={(e) => e.target.blur()}
                    required
                    placeholder="e.g., 45000"
                  />
                </div>
                <div className="form-group">
                  <label>Condition</label>
                  <select name="condition" value={editFormData.condition} onChange={handleInputChange}>
                    <option value="New">New</option>
                    <option value="Like New">Like New</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                    <option value="Used">Used</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Locality</label>
                  <input
                    type="text"
                    name="locality"
                    value={editFormData.locality}
                    onChange={handleInputChange}
                    placeholder="e.g., Andheri West"
                  />
                </div>
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={editFormData.city}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Mumbai"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  rows="4"
                  value={editFormData.description}
                  onChange={handleInputChange}
                  required
                  placeholder="Provide details about the item's condition, features, etc."
                ></textarea>
              </div>

              {/* STYLIZED SOLD TOGGLE SWITCH */}
              <div className="form-group sold-toggle-group">
                <label className="toggle-label">Mark as Sold</label>
                <label className="switch">
                  <input
                    type="checkbox"
                    name="is_sold"
                    checked={editFormData.is_sold}
                    onChange={handleInputChange}
                  />
                  <span className="slider round"></span>
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btnRefactored"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button type="submit" className="save-btnRefactored" disabled={updating}>
                  {updating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyListings;